import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { CoreUIMediaItem } from '../../media/core-ui-media.types';
import { CoreUIMediaHoverPreviewPanelComponent } from './media-hover-preview-panel.component';

const DEFAULT_PREVIEW_DELAY_MS = 600;
const DEFAULT_MIN_VIEWPORT_WIDTH_PX = 769;
const PREVIEW_VIEWPORT_MARGIN_PX = 12;
const PREVIEW_HORIZONTAL_GAP_PX = 8;
const PREVIEW_BOTTOM_OFFSET_PX = 8;

let previewId = 0;

@Directive({
  selector: '[coreUiMediaHoverPreview]',
  standalone: false,
})
export class CoreUIMediaHoverPreviewDirective implements OnDestroy {
  @Input('coreUiMediaHoverPreview') item: CoreUIMediaItem | null = null;
  @Input() mediaHoverPreviewDelay = DEFAULT_PREVIEW_DELAY_MS;
  @Input() mediaHoverPreviewMinWidth = DEFAULT_MIN_VIEWPORT_WIDTH_PX;
  @Input() mediaHoverPreviewDisabled = false;

  private readonly panelId = `core-ui-media-hover-preview-${++previewId}`;
  private readonly positions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetX: PREVIEW_HORIZONTAL_GAP_PX,
      offsetY: PREVIEW_BOTTOM_OFFSET_PX,
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetX: -PREVIEW_HORIZONTAL_GAP_PX,
      offsetY: PREVIEW_BOTTOM_OFFSET_PX,
    },
  ];
  private overlayRef?: OverlayRef;
  private openTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly overlay: Overlay,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  @HostListener('mouseenter')
  scheduleOpen(): void {
    this.cancelOpen();
    if (!this.canOpen()) return;

    const delay = Math.max(0, Number(this.mediaHoverPreviewDelay) || 0);
    this.openTimer = setTimeout(() => {
      this.openTimer = undefined;
      this.open();
    }, delay);
  }

  @HostListener('click')
  @HostListener('mouseleave')
  close(): void {
    this.cancelOpen();
    this.overlayRef?.detach();
  }

  ngOnDestroy(): void {
    this.cancelOpen();
    this.overlayRef?.dispose();
  }

  private canOpen(): boolean {
    if (this.mediaHoverPreviewDisabled || !this.item?.url) return false;

    const viewport = this.document.defaultView;
    if (!viewport) return false;
    return viewport.innerWidth >= Math.max(0, Number(this.mediaHoverPreviewMinWidth) || 0);
  }

  private open(): void {
    if (!this.canOpen()) return;

    const overlayRef = this.getOverlayRef();
    if (overlayRef.hasAttached()) return;

    const componentRef = overlayRef.attach(new ComponentPortal(CoreUIMediaHoverPreviewPanelComponent));
    componentRef.setInput('item', this.item);
    componentRef.setInput('panelId', this.panelId);
    this.renderer.setAttribute(this.elementRef.nativeElement, 'aria-describedby', this.panelId);
  }

  private getOverlayRef(): OverlayRef {
    if (this.overlayRef) return this.overlayRef;

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions(this.positions)
      .withPush(true)
      .withViewportMargin(PREVIEW_VIEWPORT_MARGIN_PX);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });
    this.overlayRef.detachments().subscribe(() => {
      this.renderer.removeAttribute(this.elementRef.nativeElement, 'aria-describedby');
    });
    return this.overlayRef;
  }

  private cancelOpen(): void {
    clearTimeout(this.openTimer);
    this.openTimer = undefined;
  }
}
